import { GraphQLResult } from '@aws-amplify/api-graphql';
import { createAsyncThunk, createEntityAdapter, createSlice, isAnyOf, PayloadAction } from '@reduxjs/toolkit';
import {
  AgaveSubJob,
  AgaveEquipment,
  AgaveEmCostCode,
  AgaveEmCostType,
  CreateAgaveCostCodeInput,
  CreateAgaveCostTypeInput,
  CreateAgaveGlCodeInput,
  CreateAgaveLocationInput,
  CreateAgaveDepartmentInput,
  CreateAgaveSubaccountInput,
  CreateAgaveEmployeeEntityInput,
  CreateAgaveProjectInput,
  CreateAgaveVendorInput,
  CodingOptions,
  CreateAgaveExpenseAPInvoice,
  CreateAgaveExpenseAPInvoiceInput,
  FlexCodeTransactionStatus,
  FlexGlCode,
  GetProcoreProjectsPayloadInput,
  GetProcoreWBSCodesPayloadInput,
  HighnoteTransactionEvent,
  GetHighnoteTransactionEventQuery,
  FlexCode,
  FlexCostCode,
  FlexCostType,
  ERPPostingStatus,
  ReturnedStatus,
  WorkflowStatus,
} from 'API';
import type { SearchSpxTransactionsInput } from 'CDK_API';
import {
  SEARCH_SORT_DIRECTION,
  TRANSACTION_SORT_FIELD,
  SearchSortDirection,
  LocalTransactionSortField,
  LocalTransactionFilterInput,
} from 'app/util/searchTypes';
import { RootState } from 'app/store/rootReducer';
import { SortOption } from 'util/filtering/models';
import { graphqlOperation } from 'aws-amplify';
import { endOfMonth, startOfMonth } from 'date-fns';
import { easternDayBounds } from 'util/dateUtils';
// SEE-1412: not `graphql/queries` — the auto-generated document selects the cardholder's
// owner-only PII on the nested user and returns Unauthorized for each field.
import { getHighnoteTransactionEventForReview } from 'graphql_custom/queries';
import { isNotNullOrUndefined } from 'util/typeGuards';
import { getCardDetails } from './CardDataSlice/cardsThunks';
import { createOrUpdateDirectCost } from './procoreSlice';
import { graphqlRequest } from 'app/services/middleware';
// The thunks return `SpxTransactionsPage`, whose items keep the Amplify
// `HighnoteTransactionEvent` type, so the reducers and the entity adapter typed against it
// are untouched by the transport change.
import {
  SpxTransactionsPage,
  transactionService,
  UpdateTransactionInput,
} from 'app/services/transaction.service';
import type { SplitAwareTransaction } from 'app/util/splitRows';

const entityAdapter = createEntityAdapter<HighnoteTransactionEvent>({
  selectId: (transaction) => transaction.id,
});

// SEE-1874: how long a Return all's fold-back is held against the search index's lag.
const FOLD_BACK_TTL_MS = 60_000;

interface FoldedBack {
  childIds: string[];
  /** When the splits were returned (ms since epoch). */
  at: number;
}

const initialState = entityAdapter.getInitialState({
  nextToken: undefined as string | undefined,
  total: 0,
  loading: false,
  loadingMore: false,
  // SEE-1392: the read path's counterpart to hadErrorUpdating/updateError below.
  // Without these a rejected list fetch is indistinguishable from an empty
  // account — .rejected only cleared the spinners, so the table fell through to
  // TransactionsEmptyState and told the user they have no transactions. Reachable
  // once SEE-301 repoints searchSpxTransactions: the CDK dispatcher raises where
  // the Amplify one returned the failure as HTTP 200 data.
  //
  // SEE-1583 added a second flag, `loadFailed`, for the same condition on the
  // filter path: the rows are deliberately left as they were, so the page must
  // say the table no longer reflects what was asked for, or a filter chip sits
  // over unfiltered rows with nothing to explain it. That snackbar now reads this
  // pair. Merged to one flag deliberately — this one is set on strictly more
  // actions (load-more and the mobileActions sub-slice included) and carries the
  // message, and two booleans for one condition only drift apart.
  hadErrorLoading: false,
  loadError: null as string | null,
  isUpdating: false,
  hadErrorUpdating: false,
  // SEE-930: carries the rejected-thunk error.message so the snackbar can
  // surface the real reason (e.g. group-admin out-of-scope deny) instead of
  // the generic transactionSidesheet.errorUpdating fallback. Cleared on
  // pending and on every fulfilled write that resets hadErrorUpdating.
  updateError: null as string | null,
  postingTransactionIds: [] as string[],
  latestRefetchSeqById: {} as Record<string, number>,
  // SEE-974: per-transaction coding-type pick from the "Transaction type"
  // dropdown ('gl' | 'project' | 'equipment'). Client-only/session state — it
  // isn't a stored field; an explicit Equipment/Project pick drives the column
  // greying even before any codes are entered. GL (or no pick) falls back to
  // deriving the type from whatever is coded.
  codingTypeOverridesById: {} as Record<string, string>,
  // SEE-944: highest clientWriteSeq seen per transaction for agave coding writes.
  // A .fulfilled whose meta.arg.clientWriteSeq is below this was superseded and
  // is dropped to prevent stale field resurrection (see updateTransaction.fulfilled).
  latestAgaveCodingSeqById: {} as Record<string, number>,
  // SEE-1874: split parents whose splits were all returned, with the split ids and when.
  // The OpenSearch refetch right after still holds the old split rows for a few seconds;
  // until it reports the parent unsplit, those rows are dropped and the parent shown
  // folded back. Cleared on Submit all, which brings splits back under the same ids, and
  // dropped after FOLD_BACK_TTL_MS so a resubmit made elsewhere is never hidden.
  recentlyFoldedBack: {} as Record<string, FoldedBack>,
  // SEE-1874: rows the search has returned so far, counted before any fold-back drops
  // split rows. The table pages against this: counting the shown rows would read a
  // folded-back page as "not full, more coming" and pin the skeleton.
  fetchedCount: 0,
  mobileActions: {
    transactions: [] as HighnoteTransactionEvent[],
    loading: false,
    // SEE-1392: same pair as above, for the mobile Actions list.
    hadErrorLoading: false,
    loadError: null as string | null,
    total: 0,
    // Ids the cardholder just resubmitted. The (eventually consistent) OpenSearch
    // Actions query still reports them as RETURNED for a moment, so we locally
    // force returnedStatus=RESUBMITTED until the index catches up. This keeps them
    // out of the Returned tab while letting the normal actionable filter decide
    // whether they still belong in All (e.g. still missing a receipt).
    recentlyResubmittedIds: [] as string[],
  },
});

export type TransactionsState = typeof initialState;

export interface GetTransactionsArgs {
  paidolId: string;
  paymentCardId?: string;
  paymentCardIds?: string[];
  nextToken?: string;
  filterDate?: Date | null;
  searchTerms?: string | null;
  sortField?: LocalTransactionSortField;
  sortOrder?: SearchSortDirection;
  shouldGetCardDetails?: boolean;
}

export interface UpdateTransactionArgs {
  id: string;
  note?: string;
  jobCodeId?: string | null;
  procoreProject?: GetProcoreProjectsPayloadInput | null;
  procoreWBSCode?: GetProcoreWBSCodesPayloadInput | null;
  agaveProject?: CreateAgaveProjectInput | null;
  agaveGlCode?: CreateAgaveGlCodeInput | null;
  flexGlCode?: FlexGlCode | null;
  flexCode?: FlexCode | null;
  flexCostCode?: FlexCostCode | null;
  flexCostType?: FlexCostType | null;
  agaveCostCode?: CreateAgaveCostCodeInput | null;
  agaveCostType?: CreateAgaveCostTypeInput | null;
  agaveVendor?: CreateAgaveVendorInput | null;
  agaveSubJob?: AgaveSubJob | null;
  // SEE-974 — Vista equipment coding. Only the id is persisted (the thunk maps
  // agaveEquipment?.id -> agaveEquipmentId), mirroring the other coding fields.
  agaveEquipment?: AgaveEquipment | null;
  agaveEmCostCode?: AgaveEmCostCode | null;
  agaveEmCostType?: AgaveEmCostType | null;
  agaveExpense?: CreateAgaveExpenseAPInvoice | null;
  agaveLocation?: CreateAgaveLocationInput | null;
  agaveDepartment?: CreateAgaveDepartmentInput | null;
  agaveSubaccount?: CreateAgaveSubaccountInput | null;
  agaveEmployee?: CreateAgaveEmployeeEntityInput | null;
  // SEE-974 — explicitly-selected coding type, persisted UPPERCASE
  // (GL/PROJECT/EQUIPMENT) so the server type filter can match the selected type
  // even on rows with no coding values.
  codingType?: string | null;
  // SEE-1135 — per-transaction Sage Intacct billable item. Flat passthroughs (not
  // an entity object mapped to an id like the agave* fields): the item id is the
  // billable signal and the name is denormalized for display.
  erpBillableItemId?: string | null;
  erpBillableItemName?: string | null;
  // SEE-1390: per-transaction billable opt-out — auto-set true when an item
  // lands; explicit false posts non-billable while the item stays coded.
  erpBillable?: boolean | null;
  accountingDate?: string | null;
  receiptPath?: string;
  receiptId?: string;
  approveWithoutReceipts?: boolean;
  approveWithoutNotes?: boolean;
  erpApprovalStatus?: boolean;
  erpBypassStatus?: boolean;
  erpPostingStatus?: ERPPostingStatus | null;
  flexCodeTransactionStatus?: FlexCodeTransactionStatus | null;
  workflowStatus?: WorkflowStatus | null;
  returnedStatus?: ReturnedStatus | null;
  fuelVehicleId?: string | null;
  fuelOdometerReading?: number | null;
  fuelGallons?: number | null;
  fuelCostPerGallon?: number | null;
  // SEE-944: client-side monotonic sequence stamped on agave coding writes so a
  // slow, superseded write (e.g. a project's auto-fill landing after the project
  // was removed) can't resurrect stale location/department in .fulfilled. Mirrors
  // the latestRefetchSeqById pattern used for refetch ordering. Client-only —
  // never forwarded to the backend mutation.
  clientWriteSeq?: number;
}

export type SetTransactionPostingPayload = { id: string; isPosting: boolean };

// Matches the first entry in rowsPerPageOptions [25, 50, 100] in UniversalTableRenderer.
// Callers should always pass limit explicitly (driven by the user's rows-per-page selection).
// This default only applies if a caller omits limit entirely.
export const DEFAULT_TRANSACTIONS_PAGE_SIZE = 25;

export interface GetTransactionsWithFiltersArgs {
  nextToken?: string;
  filterObject?: LocalTransactionFilterInput;
  sortObject?: SortOption;
  shouldGetCardDetails?: boolean;
  limit?: number;
}

export const getTransactionsWithFilters = createAsyncThunk(
  'overview/transactions/GetTransactionsWithFiltersArgs',
  async (
    {
      filterObject,
      sortObject,
      nextToken,
      shouldGetCardDetails = true,
      limit = DEFAULT_TRANSACTIONS_PAGE_SIZE,
    }: GetTransactionsWithFiltersArgs,
    { dispatch }
  ) => {
    const variables = {
      filter: filterObject,
      sort: sortObject?.field ? sortObject : undefined,
      nextToken,
      limit,
    };

    const payload = await transactionService.searchSpxTransactions(variables as SearchSpxTransactionsInput);

    if (shouldGetCardDetails) {
      const paymentCardIds = new Set(
        payload?.items?.map((item) => item?.paymentCardId).filter(isNotNullOrUndefined)
      );

      paymentCardIds.forEach((id) => {
        dispatch(getCardDetails(id));
      });
    }

    return payload as SpxTransactionsPage | null;
  }
);

export const getMobileActionsTransactions = createAsyncThunk(
  'overview/transactions/getMobileActionsTransactions',
  async ({ filterObject, limit = 10000 }: { filterObject: LocalTransactionFilterInput; limit?: number }) => {
    const variables = {
      filter: filterObject,
      limit,
    };

    const payload = await transactionService.searchSpxTransactions(variables as SearchSpxTransactionsInput);
    return payload as SpxTransactionsPage | null;
  }
);

export const getTransactions = createAsyncThunk(
  'overview/transactions/getTransactions',
  async (
    {
      paidolId,
      paymentCardId,
      nextToken,
      filterDate,
      searchTerms,
      sortField = TRANSACTION_SORT_FIELD.eventDate,
      sortOrder = SEARCH_SORT_DIRECTION.desc,
      shouldGetCardDetails = true,
      paymentCardIds,
    }: GetTransactionsArgs,
    { dispatch }
  ) => {
    const variables: SearchSpxTransactionsInput = {
      filter: {
        paidolId: { eq: paidolId },
        ...(filterDate && {
          eventDate: easternDayBounds(startOfMonth(filterDate), endOfMonth(filterDate)),
        }),
        or: [
          ...(paymentCardId ? [{ paymentCardId: { eq: paymentCardId } }] : []),
          ...(paymentCardIds ? paymentCardIds.map((id) => ({ paymentCardId: { eq: id } })) : []),
        ],
      },
      sort: {
        field: sortField,
        direction: sortOrder,
      },
      nextToken,
      limit: 25,
    };
    const payload = await transactionService.searchSpxTransactions(variables as SearchSpxTransactionsInput);

    if (shouldGetCardDetails) {
      const paymentCardIds = new Set(
        payload?.items?.map((item) => item?.paymentCardId).filter(isNotNullOrUndefined)
      );

      paymentCardIds.forEach((id) => {
        dispatch(getCardDetails(id));
      });
    }

    return payload as SpxTransactionsPage | null;
  }
);

export const updateTransaction = createAsyncThunk(
  'overview/transactions/updateTransaction',
  async (args: UpdateTransactionArgs) => {
    const {
      id,
      note,
      jobCodeId,
      procoreProject,
      procoreWBSCode,
      agaveCostType,
      agaveCostCode,
      agaveProject,
      agaveVendor,
      agaveSubJob,
      agaveExpense,
      agaveLocation,
      agaveDepartment,
      agaveEmployee,
      receiptPath,
      erpApprovalStatus,
      erpBypassStatus,
      erpPostingStatus,
      flexCodeTransactionStatus,
      workflowStatus,
      returnedStatus,
      receiptId,
      agaveGlCode,
      flexGlCode,
      flexCode,
      flexCostCode,
      flexCostType,
      approveWithoutReceipts,
      approveWithoutNotes,
      fuelVehicleId,
      fuelOdometerReading,
      fuelGallons,
      fuelCostPerGallon,
      accountingDate,
    } = args;

    const input: UpdateTransactionInput = {
      id,
      ...(note !== undefined && { note }),
      ...(jobCodeId !== undefined && { jobCodeId }),
      ...(procoreProject !== undefined && {
        procoreProjectId: procoreProject?.id ?? null,
        procoreProject: procoreProject ?? null,
      }),
      ...(procoreWBSCode !== undefined && {
        procoreWBSCodeId: procoreWBSCode?.id ?? null,
        procoreWBSCode: procoreWBSCode ?? null,
      }),
      ...(agaveCostType !== undefined && { agaveCostTypeId: agaveCostType?.id ?? null }),
      ...(agaveCostCode !== undefined && { agaveCostCodeId: agaveCostCode?.id ?? null }),
      ...(agaveProject !== undefined && { agaveProjectId: agaveProject?.id ?? null }),
      ...(agaveGlCode !== undefined && { agaveGlCodeId: agaveGlCode?.id ?? null }),
      ...(flexGlCode !== undefined && { flexGlCodeId: flexGlCode?.id ?? null }),
      ...(flexCode !== undefined && { flexCodeId: flexCode?.id ?? null }),
      ...(flexCostCode !== undefined && { flexCostCodeId: flexCostCode?.id ?? null }),
      ...(flexCostType !== undefined && { flexCostTypeId: flexCostType?.id ?? null }),
      ...(agaveVendor !== undefined && { agaveVendorId: agaveVendor?.id ?? null }),
      ...(agaveSubJob !== undefined && { agaveSubJobId: agaveSubJob?.id ?? null }),
      ...(agaveExpense !== undefined && {
        agaveExpenseId: agaveExpense?.id ?? null,
        agaveExpense: agaveExpense as CreateAgaveExpenseAPInvoiceInput | null,
      }),
      ...(agaveLocation !== undefined && { agaveLocationId: agaveLocation?.id ?? null }),
      ...(agaveDepartment !== undefined && { agaveDepartmentId: agaveDepartment?.id ?? null }),
      ...(agaveEmployee !== undefined && { agaveEmployeeId: agaveEmployee?.id ?? null }),
      ...(accountingDate !== undefined && { accountingDate }),
      ...(receiptPath !== undefined && { receiptPath }),
      ...(receiptId !== undefined && { receiptId }),
      ...(approveWithoutReceipts !== undefined && { approveWithoutReceipts }),
      ...(approveWithoutNotes !== undefined && { approveWithoutNotes }),
      ...(fuelVehicleId !== undefined && { fuelVehicleId }),
      ...(fuelOdometerReading !== undefined && { fuelOdometerReading }),
      ...(fuelGallons !== undefined && { fuelGallons }),
      ...(fuelCostPerGallon !== undefined && { fuelCostPerGallon }),
      ...(erpApprovalStatus !== undefined && { erpApprovalStatus }),
      ...(erpBypassStatus !== undefined && { erpBypassStatus }),
      ...(erpPostingStatus !== undefined && { erpPostingStatus }),
      ...(flexCodeTransactionStatus !== undefined && { flexCodeTransactionStatus }),
      ...(workflowStatus !== undefined && { workflowStatus }),
      ...(returnedStatus !== undefined && { returnedStatus }),
    };

    const updated = await transactionService.updateTransaction(input);
    return updated as unknown as HighnoteTransactionEvent;
  }
);

// Must be called synchronously at the start of any update thunk (before any
// await), so the user's click order — not the network response order —
// decides which refetch wins. Stale refetches are detected in the .fulfilled
// reducers by comparing seq against latestRefetchSeqById[id].
let refetchTransactionSeq = 0;

export interface RefetchTransactionResult {
  seq: number;
  transaction: HighnoteTransactionEvent;
}

function assignRefetchSeq(
  id: string,
  dispatch: (action: { type: string; payload: { id: string; seq: number } }) => unknown
): number {
  const seq = ++refetchTransactionSeq;
  dispatch(transactionsSlice.actions.markRefetchSeqDispatched({ id, seq }));
  return seq;
}

export const refetchTransaction = createAsyncThunk(
  'overview/transactions/refetchTransaction',
  async ({ id, seq }: { id: string; seq?: number }, { dispatch }): Promise<RefetchTransactionResult> => {
    // Standalone callers (no parent thunk) get a seq assigned here.
    const effectiveSeq = seq ?? assignRefetchSeq(id, dispatch);
    const res = (await graphqlRequest(
      graphqlOperation(getHighnoteTransactionEventForReview, { id }),
      dispatch
    )) as GraphQLResult<GetHighnoteTransactionEventQuery>;
    return {
      seq: effectiveSeq,
      transaction: res?.data?.getHighnoteTransactionEvent as HighnoteTransactionEvent,
    };
  }
);

export const updateTransactionReceipt = createAsyncThunk(
  'overview/transactions/updateTransactionReceipt',
  async ({
    receiptPath = null,
    receiptId = null,
    id,
  }: {
    id: string;
    paidolId: string;
    receiptPath?: string | null;
    receiptId?: string | null;
  }) => {
    const updated = await transactionService.updateTransaction({
      id,
      receiptPath,
      receiptId,
    });
    return updated as unknown as HighnoteTransactionEvent;
  }
);

export const updateTransactionNote = createAsyncThunk(
  'overview/transactions/updateTransactionNote',
  async ({ note, id }: { note: string | null | undefined; id: string; paidolId: string }) => {
    const updated = await transactionService.updateTransaction({ id, note });
    return updated as unknown as HighnoteTransactionEvent;
  }
);

export const updateTransactionFlexCode = createAsyncThunk(
  'overview/transactions/updateTransactionFlexCode',
  async (
    {
      id,
      flexCodeId,
    }: {
      paidolId: string;
      id: string;
      flexCodeId: string | null;
    },
    { dispatch }
  ) => {
    const seq = assignRefetchSeq(id, dispatch);
    await transactionService.updateTransaction({ id, flexCodeId });
    return await dispatch(refetchTransaction({ id, seq })).unwrap();
  }
);

export const updateTransactionFlexGlCode = createAsyncThunk(
  'overview/transactions/updateTransactionFlexGlCode',
  async (
    {
      id,
      flexGlCodeId,
    }: {
      paidolId: string;
      id: string;
      flexGlCodeId: string | null;
    },
    { dispatch }
  ) => {
    const seq = assignRefetchSeq(id, dispatch);
    await transactionService.updateTransaction({ id, flexGlCodeId });
    return await dispatch(refetchTransaction({ id, seq })).unwrap();
  }
);

export const updateTransactionFlexCostCode = createAsyncThunk(
  'overview/transactions/updateTransactionFlexCostCode',
  async (
    {
      id,
      flexCostCodeId,
    }: {
      paidolId: string;
      id: string;
      flexCostCodeId: string | null;
    },
    { dispatch }
  ) => {
    const seq = assignRefetchSeq(id, dispatch);
    await transactionService.updateTransaction({ id, flexCostCodeId });
    return await dispatch(refetchTransaction({ id, seq })).unwrap();
  }
);

// `updateTransactionCodingOption` historically called the auto-gen update with
// only id/paidolId (a no-op write that bumped updatedAt and returned the row).
// The new IAM-only Lambda rejects empty updates, so we skip the mutation and
// refetch directly. The Redux reducer only consumes the truthy payload.
export const updateTransactionCodingOption = createAsyncThunk(
  'overview/transactions/updateTransactionCodingOption',
  async (
    {
      id,
    }: {
      paidolId: string;
      id: string;
      selectedCodingOption: CodingOptions;
    },
    { dispatch }
  ) => {
    const seq = assignRefetchSeq(id, dispatch);
    return await dispatch(refetchTransaction({ id, seq })).unwrap();
  }
);

export const updateTransactionFlexCostType = createAsyncThunk(
  'overview/transactions/updateTransactionFlexCostType',
  async (
    {
      id,
      flexCostTypeId,
    }: {
      paidolId: string;
      id: string;
      flexCostTypeId: string | null;
    },
    { dispatch }
  ) => {
    const seq = assignRefetchSeq(id, dispatch);
    await transactionService.updateTransaction({ id, flexCostTypeId });
    return await dispatch(refetchTransaction({ id, seq })).unwrap();
  }
);

export const updateTransactionFlexVendor = createAsyncThunk(
  'overview/transactions/updateTransactionFlexVendor',
  async (
    {
      id,
      flexVendorId,
    }: {
      paidolId: string;
      id: string;
      flexVendorId: string | null;
    },
    { dispatch }
  ) => {
    const seq = assignRefetchSeq(id, dispatch);
    await transactionService.updateTransaction({ id, flexVendorId });
    return await dispatch(refetchTransaction({ id, seq })).unwrap();
  }
);

export const updateReceiptToggle = createAsyncThunk(
  'overview/transactions/updateReceiptToggle',
  async (
    {
      id,
      approveWithoutReceipts,
    }: {
      paidolId: string;
      id: string;
      approveWithoutReceipts: boolean;
    },
    { dispatch }
  ) => {
    const seq = assignRefetchSeq(id, dispatch);
    await transactionService.updateTransaction({ id, approveWithoutReceipts });
    return await dispatch(refetchTransaction({ id, seq })).unwrap();
  }
);

export const updateNotesToggle = createAsyncThunk(
  'overview/transactions/updateNotesToggle',
  async (
    {
      id,
      approveWithoutNotes,
    }: {
      paidolId: string;
      id: string;
      approveWithoutNotes: boolean;
    },
    { dispatch }
  ) => {
    const seq = assignRefetchSeq(id, dispatch);
    await transactionService.updateTransaction({ id, approveWithoutNotes });
    return await dispatch(refetchTransaction({ id, seq })).unwrap();
  }
);

// Refetches like the flex path: the backend derives workflowStatus from this write,
// and the updateTransaction reducer only copies fields the caller sent.
type SavedGroupContext = Pick<HighnoteTransactionEvent, 'groupApprovalLevels' | 'owningProjectGroupIds'>;

// The single-row refetch can't resolve a row's group steps, and the list's count dates
// from its last search, so the save returns them. Absent means the paidol doesn't use
// group approvals.
const savedGroupContext = (saved: Record<string, unknown>): SavedGroupContext | undefined =>
  saved.groupApprovalLevels === undefined
    ? undefined
    : {
        groupApprovalLevels: saved.groupApprovalLevels as number,
        owningProjectGroupIds: saved.owningProjectGroupIds as string[],
      };

export const markErpTransactionReviewed = createAsyncThunk(
  'overview/transactions/markErpTransactionReviewed',
  async ({ id }: { id: string }, { dispatch }) => {
    const seq = assignRefetchSeq(id, dispatch);
    const saved = await transactionService.updateTransaction({ id, erpApprovalStatus: true });
    const refetched = await dispatch(refetchTransaction({ id, seq })).unwrap();
    return { ...refetched, groupContext: savedGroupContext(saved) };
  }
);

export const updateFlexCodeTransactionStatus = createAsyncThunk(
  'overview/transactions/updateFlexCodeTransactionStatus',
  async (
    {
      id,
      flexCodeTransactionStatus,
    }: {
      paidolId: string;
      id: string;
      flexCodeTransactionStatus: FlexCodeTransactionStatus;
    },
    { dispatch }
  ) => {
    const seq = assignRefetchSeq(id, dispatch);
    const saved = await transactionService.updateTransaction({ id, flexCodeTransactionStatus });
    const refetched = await dispatch(refetchTransaction({ id, seq })).unwrap();
    return { ...refetched, groupContext: savedGroupContext(saved) };
  }
);

export const bulkUpdateFlexCodeStatus = createAsyncThunk(
  'overview/transactions/bulkUpdateFlexCodeStatus',
  async ({
    ids,
    status,
  }: {
    ids: string[];
    status: FlexCodeTransactionStatus;
    paidolId: string;
    originalStatuses: { id: string; status: FlexCodeTransactionStatus }[];
  }) => {
    const updates = ids.map((id) =>
      transactionService.updateTransaction({ id, flexCodeTransactionStatus: status })
    );

    const results = await Promise.allSettled(updates);

    return results.map((result, index) => {
      if (result.status === 'fulfilled') {
        return {
          id: ids[index],
          transaction: result.value as unknown as HighnoteTransactionEvent,
        };
      } else {
        return {
          id: ids[index],
          transaction: undefined,
        };
      }
    });
  }
);

// SEE-1874: show a returned split as the single returned transaction it now is. Returns
// how many split rows were dropped.
function foldBackRows(state: TransactionsState, parentId: string, childIds: string[]): number {
  const present = childIds.filter((id) => state.entities[id]);
  entityAdapter.removeMany(state, present);
  if (state.entities[parentId]) {
    const changes: Partial<SplitAwareTransaction> = {
      isSplitParent: null,
      splitChildIds: null,
      splitId: null,
      returnedStatus: ReturnedStatus.RETURNED,
    };
    entityAdapter.updateOne(state, { id: parentId, changes: changes as Partial<HighnoteTransactionEvent> });
  }
  return present.length;
}

// After a list load: keep folding back what the index still reports as split. Forget a
// parent once any page reports it unsplit, or once the index has had well past its lag
// to catch up: past that, a split parent is a resubmit (same child ids), not stale.
function settleFoldedBack(state: TransactionsState, transactions: HighnoteTransactionEvent[], now: number) {
  for (const [parentId, { childIds, at }] of Object.entries(state.recentlyFoldedBack ?? {})) {
    const parent = transactions.find((tx) => tx?.id === parentId) as SplitAwareTransaction | undefined;
    if ((parent && !parent.isSplitParent) || now - at > FOLD_BACK_TTL_MS) {
      delete state.recentlyFoldedBack[parentId];
      continue;
    }
    state.total = Math.max(0, state.total - foldBackRows(state, parentId, childIds));
  }
}

const transactionsSlice = createSlice({
  name: 'overview/transactions',
  initialState,
  reducers: {
    resetTransactionsSlice: () => initialState,
    setTransactionPosting: (state, action: PayloadAction<SetTransactionPostingPayload>) => {
      const { id, isPosting } = action.payload;
      if (isPosting) {
        if (!state.postingTransactionIds.includes(id)) {
          state.postingTransactionIds.push(id);
        }
      } else {
        state.postingTransactionIds = state.postingTransactionIds.filter(
          (transactionId) => transactionId !== id
        );
      }
    },
    applyLocalTransactionUpdates: (
      state,
      action: PayloadAction<{ id: string; changes: Partial<HighnoteTransactionEvent> }[]>
    ) => {
      entityAdapter.updateMany(state, action.payload);
    },
    markRefetchSeqDispatched: (state, action: PayloadAction<{ id: string; seq: number }>) => {
      const { id, seq } = action.payload;
      if (seq > (state.latestRefetchSeqById[id] ?? 0)) {
        state.latestRefetchSeqById[id] = seq;
      }
    },
    // A cardholder resubmitted a returned transaction. Optimistically relabel it
    // RESUBMITTED (mirroring the server's review reset) and remember the id, so the
    // stale OpenSearch refetch can't resurface it as RETURNED. It leaves the
    // Returned tab immediately; the actionable filter still decides whether it
    // stays under All (e.g. it's still missing a receipt).
    markTransactionResubmitted: (state, action: PayloadAction<string>) => {
      const id = action.payload;
      if (!state.mobileActions.recentlyResubmittedIds.includes(id)) {
        state.mobileActions.recentlyResubmittedIds.push(id);
      }
      state.mobileActions.transactions = state.mobileActions.transactions.map((t) =>
        t.id === id
          ? {
              ...t,
              returnedStatus: ReturnedStatus.RESUBMITTED,
              flexCodeTransactionStatus: FlexCodeTransactionStatus.NEEDS_REVIEW,
              erpApprovalStatus: false,
            }
          : t
      );
    },
    markSplitFoldedBack: {
      reducer: (state, action: PayloadAction<{ parentId: string; childIds: string[]; at: number }>) => {
        const { parentId, childIds, at } = action.payload;
        state.recentlyFoldedBack[parentId] = { childIds, at };
        state.total = Math.max(0, state.total - foldBackRows(state, parentId, childIds));
      },
      prepare: (payload: { parentId: string; childIds: string[] }) => ({
        payload: { ...payload, at: Date.now() },
      }),
    },
    clearSplitFoldedBack: (state, action: PayloadAction<string>) => {
      delete state.recentlyFoldedBack[action.payload];
    },
    // SEE-974: record the "Transaction type" dropdown pick for a transaction.
    setCodingTypeOverride: (state, action: PayloadAction<{ id: string; codingType: string }>) => {
      state.codingTypeOverridesById[action.payload.id] = action.payload.codingType;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getTransactions.pending, (state, action) => {
      state.hadErrorLoading = false;
      state.loadError = null;
      if (action.meta.arg.nextToken) {
        state.loadingMore = true;
      } else {
        state.loading = true;
      }
    });

    builder.addCase(getTransactionsWithFilters.pending, (state, action) => {
      state.hadErrorLoading = false;
      state.loadError = null;
      if (action.meta.arg.nextToken) {
        state.loadingMore = true;
      } else {
        state.loading = true;
      }
    });

    builder.addCase(getTransactionsWithFilters.fulfilled, (state, action) => {
      if (action.payload?.items) {
        const transactions = action.payload.items
          .filter(isNotNullOrUndefined)
          .map((i) => i as HighnoteTransactionEvent);

        if (!action.meta.arg.nextToken) {
          entityAdapter.removeAll(state);
          state.latestRefetchSeqById = {};
        }

        entityAdapter.addMany(state, transactions);
      }

      state.nextToken = action.payload?.nextToken ?? undefined;
      state.total = action.payload?.total ?? 0;
      const fetched = (action.payload?.items ?? []).filter(isNotNullOrUndefined).length;
      state.fetchedCount = action.meta.arg.nextToken ? state.fetchedCount + fetched : fetched;
      settleFoldedBack(state, (action.payload?.items ?? []) as HighnoteTransactionEvent[], Date.now());
      state.loading = false;
      state.loadingMore = false;
      state.hadErrorLoading = false;
      state.loadError = null;
    });

    // Without this, a failed load-more leaves loadingMore=true forever
    // and the table stays in a permanent skeleton state (SEE-780).
    builder.addCase(getTransactionsWithFilters.rejected, (state, action) => {
      state.loading = false;
      state.loadingMore = false;
      state.hadErrorLoading = true;
      state.loadError = action.error?.message ?? null;
    });

    builder.addCase(getMobileActionsTransactions.pending, (state) => {
      state.mobileActions.loading = true;
      state.mobileActions.hadErrorLoading = false;
      state.mobileActions.loadError = null;
    });

    builder.addCase(getMobileActionsTransactions.fulfilled, (state, action) => {
      if (action.payload?.items) {
        const transactions = action.payload.items
          .filter(isNotNullOrUndefined)
          .map((i) => i as HighnoteTransactionEvent);

        // The index lags the resubmit write, so it still reports these as RETURNED
        // for a moment. Keep overriding ids the server still calls RETURNED; drop
        // the rest (index caught up — trust the server, which now reports the
        // resubmitted state). Relabel the stale ones to RESUBMITTED so they leave
        // the Returned tab while the actionable filter keeps the still-incomplete
        // ones under All.
        const stillStale = state.mobileActions.recentlyResubmittedIds.filter((id) =>
          transactions.some((tx) => tx.id === id && tx.returnedStatus === ReturnedStatus.RETURNED)
        );
        state.mobileActions.recentlyResubmittedIds = stillStale;

        state.mobileActions.transactions = transactions.map((tx) =>
          stillStale.includes(tx.id)
            ? {
                ...tx,
                returnedStatus: ReturnedStatus.RESUBMITTED,
                flexCodeTransactionStatus: FlexCodeTransactionStatus.NEEDS_REVIEW,
                erpApprovalStatus: false,
              }
            : tx
        );
        // Also add to entities so updates can find them
        entityAdapter.upsertMany(state, transactions);
      }

      state.mobileActions.total = action.payload?.total ?? 0;
      state.mobileActions.loading = false;
      state.mobileActions.hadErrorLoading = false;
      state.mobileActions.loadError = null;
    });

    builder.addCase(getMobileActionsTransactions.rejected, (state, action) => {
      state.mobileActions.loading = false;
      state.mobileActions.hadErrorLoading = true;
      state.mobileActions.loadError = action.error?.message ?? null;
    });

    builder.addCase(getTransactions.fulfilled, (state, action) => {
      if (action.payload?.items) {
        const transactions = action.payload.items
          .filter(isNotNullOrUndefined)
          .map((i) => i as HighnoteTransactionEvent);

        if (!action.meta.arg.nextToken) {
          // If nextToken not specified, this is first
          // page of results. Clear out old results.
          entityAdapter.removeAll(state);
          state.latestRefetchSeqById = {};
        }

        entityAdapter.addMany(state, transactions);
      }

      state.nextToken = action.payload?.nextToken ?? undefined;
      state.total = action.payload?.total ?? 0;
      const fetched = (action.payload?.items ?? []).filter(isNotNullOrUndefined).length;
      state.fetchedCount = action.meta.arg.nextToken ? state.fetchedCount + fetched : fetched;
      settleFoldedBack(state, (action.payload?.items ?? []) as HighnoteTransactionEvent[], Date.now());
      state.loading = false;
      state.loadingMore = false;
      state.hadErrorLoading = false;
      state.loadError = null;
    });

    builder.addCase(getTransactions.rejected, (state, action) => {
      state.loading = false;
      state.loadingMore = false;
      state.hadErrorLoading = true;
      state.loadError = action.error?.message ?? null;
    });

    builder.addCase(updateTransactionNote.fulfilled, (state, action) => {
      const transaction = state.entities[action.payload.id];

      if (transaction) {
        transaction.note = action.payload.note;

        // Also update mobileActions.transactions array if this transaction is in it
        const mobileActionIndex = state.mobileActions.transactions.findIndex(
          (t) => t.id === action.payload.id
        );
        if (mobileActionIndex !== -1) {
          state.mobileActions.transactions[mobileActionIndex].note = action.payload.note;
        }
      }
    });

    builder.addCase(updateTransactionReceipt.fulfilled, (state, action) => {
      const transaction = state.entities[action.payload.id];

      if (transaction) {
        transaction.receiptPath = action.payload.receiptPath;
        transaction.receiptId = action.payload.receiptId;
      }
    });

    builder.addCase(updateTransactionFlexCode.fulfilled, (state, action) => {
      if (!action.payload?.transaction) return;
      const { seq, transaction: payload } = action.payload;
      // Stale: bail before flag clears so an in-flight newer write keeps the
      // Review button disabled until its own .fulfilled / .rejected.
      if (seq < (state.latestRefetchSeqById[payload.id] ?? 0)) return;
      state.isUpdating = false;
      state.hadErrorUpdating = false;
      const transaction = state.entities[payload.id];
      if (transaction) {
        transaction.flexCodeId = payload.flexCodeId;
        transaction.flexCode = payload.flexCode;

        // Also update mobileActions.transactions array if this transaction is in it
        const mobileActionIndex = state.mobileActions.transactions.findIndex((t) => t.id === payload.id);
        if (mobileActionIndex !== -1) {
          state.mobileActions.transactions[mobileActionIndex].flexCodeId = payload.flexCodeId;
          state.mobileActions.transactions[mobileActionIndex].flexCode = payload.flexCode;
        }
      }
    });

    builder.addCase(updateTransactionFlexCostCode.fulfilled, (state, action) => {
      if (!action.payload?.transaction) return;
      const { seq, transaction: payload } = action.payload;
      if (seq < (state.latestRefetchSeqById[payload.id] ?? 0)) return;
      state.isUpdating = false;
      state.hadErrorUpdating = false;
      const transaction = state.entities[payload.id];
      if (transaction) {
        transaction.flexCostCodeId = payload.flexCostCodeId;
        transaction.flexCostCode = payload.flexCostCode;

        // Also update mobileActions.transactions array if this transaction is in it
        const mobileActionIndex = state.mobileActions.transactions.findIndex((t) => t.id === payload.id);
        if (mobileActionIndex !== -1) {
          state.mobileActions.transactions[mobileActionIndex].flexCostCodeId = payload.flexCostCodeId;
          state.mobileActions.transactions[mobileActionIndex].flexCostCode = payload.flexCostCode;
        }
      }
    });

    builder.addCase(updateTransactionFlexCostType.fulfilled, (state, action) => {
      if (!action.payload?.transaction) return;
      const { seq, transaction: payload } = action.payload;
      if (seq < (state.latestRefetchSeqById[payload.id] ?? 0)) return;
      state.isUpdating = false;
      state.hadErrorUpdating = false;
      const transaction = state.entities[payload.id];
      if (transaction) {
        transaction.flexCostTypeId = payload.flexCostTypeId;
        transaction.flexCostType = payload.flexCostType;

        // Also update mobileActions.transactions array if this transaction is in it
        const mobileActionIndex = state.mobileActions.transactions.findIndex((t) => t.id === payload.id);
        if (mobileActionIndex !== -1) {
          state.mobileActions.transactions[mobileActionIndex].flexCostTypeId = payload.flexCostTypeId;
          state.mobileActions.transactions[mobileActionIndex].flexCostType = payload.flexCostType;
        }
      }
    });

    builder.addCase(updateTransactionFlexVendor.fulfilled, (state, action) => {
      if (!action.payload?.transaction) return;
      const { seq, transaction: payload } = action.payload;
      if (seq < (state.latestRefetchSeqById[payload.id] ?? 0)) return;
      state.isUpdating = false;
      state.hadErrorUpdating = false;
      const transaction = state.entities[payload.id];
      if (transaction) {
        transaction.flexVendorId = payload.flexVendorId;
        transaction.flexVendor = payload.flexVendor;

        // Also update mobileActions.transactions array if this transaction is in it
        const mobileActionIndex = state.mobileActions.transactions.findIndex((t) => t.id === payload.id);
        if (mobileActionIndex !== -1) {
          state.mobileActions.transactions[mobileActionIndex].flexVendorId = payload.flexVendorId;
          state.mobileActions.transactions[mobileActionIndex].flexVendor = payload.flexVendor;
        }
      }
    });

    builder.addCase(updateTransactionFlexGlCode.fulfilled, (state, action) => {
      if (!action.payload?.transaction) return;
      const { seq, transaction: payload } = action.payload;
      if (seq < (state.latestRefetchSeqById[payload.id] ?? 0)) return;
      state.isUpdating = false;
      state.hadErrorUpdating = false;
      const transaction = state.entities[payload.id];
      if (transaction) {
        transaction.flexGlCodeId = payload.flexGlCodeId;
        transaction.flexGlCode = payload.flexGlCode;

        // Also update mobileActions.transactions array if this transaction is in it
        const mobileActionIndex = state.mobileActions.transactions.findIndex((t) => t.id === payload.id);
        if (mobileActionIndex !== -1) {
          state.mobileActions.transactions[mobileActionIndex].flexGlCodeId = payload.flexGlCodeId;
          state.mobileActions.transactions[mobileActionIndex].flexGlCode = payload.flexGlCode;
        }
      }
    });

    builder.addCase(updateTransactionCodingOption.fulfilled, (state, action) => {
      if (!action.payload?.transaction) return;
      const { seq, transaction: payload } = action.payload;
      if (seq < (state.latestRefetchSeqById[payload.id] ?? 0)) return;
      state.isUpdating = false;
      state.hadErrorUpdating = false;
    });

    builder.addCase(updateTransactionReceipt.rejected, (state, action) => {
      //error handling for rejected updateTransactionReceip
      console.error('Failed to update transaction receipt:', action.error);
    });

    // SEE-944: record the latest agave coding write sequence as soon as the write
    // is dispatched, so a later .fulfilled can tell whether it was superseded.
    builder.addCase(updateTransaction.pending, (state, action) => {
      const { id, clientWriteSeq } = action.meta.arg;
      if (clientWriteSeq !== undefined && clientWriteSeq > (state.latestAgaveCodingSeqById[id] ?? 0)) {
        state.latestAgaveCodingSeqById[id] = clientWriteSeq;
      }
    });

    builder.addCase(updateTransaction.fulfilled, (state, action) => {
      if (action.payload?.id) {
        // SEE-944: drop superseded agave coding writes. A slow auto-fill write that
        // resolves after the user removed/changed the project would otherwise
        // re-apply stale location/department (the flicker). Only agave coding
        // writes stamp clientWriteSeq, so other callers are unaffected.
        const { clientWriteSeq } = action.meta.arg;
        if (
          clientWriteSeq !== undefined &&
          clientWriteSeq < (state.latestAgaveCodingSeqById[action.payload.id] ?? 0)
        ) {
          state.isUpdating = false;
          return;
        }

        const changes: any = {};

        if (action.meta.arg.note !== undefined) {
          changes['note'] = action.payload.note;
        }

        if (action.meta.arg.jobCodeId !== undefined) {
          changes['jobCodeId'] = action.payload.jobCodeId;
          if (action.payload.jobCode) {
            changes['jobCode'] = action.payload.jobCode;
          } else {
            // Mutation doesn't return nested jobCode — clear it if jobCodeId was removed,
            // otherwise preserve the existing value so the dropdown doesn't flash
            if (!action.payload.jobCodeId) {
              changes['jobCode'] = null;
            }
          }
        }

        // For nested-object dropdowns: source from action.meta.arg (the caller's input)
        // rather than action.payload, because the updateTransaction Lambda returns the
        // ID but leaves the nested join as null. Trusting the payload would clear the
        // dropdown display. The mutation's statusCode confirms the ID was saved.
        if (action.meta.arg.procoreProject !== undefined) {
          changes['procoreProject'] = action.meta.arg.procoreProject ?? null;
          changes['procoreProjectId'] = action.meta.arg.procoreProject?.id ?? null;
        }

        if (action.meta.arg.procoreWBSCode !== undefined) {
          changes['procoreWBSCode'] = action.meta.arg.procoreWBSCode ?? null;
          changes['procoreWBSCodeId'] = action.meta.arg.procoreWBSCode?.id ?? null;
        }

        if (action.meta.arg.agaveCostCode !== undefined) {
          changes['agaveCostCode'] = action.meta.arg.agaveCostCode ?? null;
          changes['agaveCostCodeId'] = action.meta.arg.agaveCostCode?.id ?? null;
        }

        if (action.meta.arg.agaveCostType !== undefined) {
          changes['agaveCostType'] = action.meta.arg.agaveCostType ?? null;
          changes['agaveCostTypeId'] = action.meta.arg.agaveCostType?.id ?? null;
        }

        if (action.meta.arg.agaveProject !== undefined) {
          changes['agaveProject'] = action.meta.arg.agaveProject ?? null;
          changes['agaveProjectId'] = action.meta.arg.agaveProject?.id ?? null;
        }

        // SEE-974: equipment coding trio — re-affirm from meta.arg on resolve,
        // mirroring every other coding field so the value survives a refetch and
        // the Equipment->Project clear (agaveEmCost* = null) is reaffirmed too.
        if (action.meta.arg.agaveEquipment !== undefined) {
          changes['agaveEquipment'] = action.meta.arg.agaveEquipment ?? null;
          changes['agaveEquipmentId'] = action.meta.arg.agaveEquipment?.id ?? null;
        }

        if (action.meta.arg.agaveEmCostCode !== undefined) {
          changes['agaveEmCostCode'] = action.meta.arg.agaveEmCostCode ?? null;
          changes['agaveEmCostCodeId'] = action.meta.arg.agaveEmCostCode?.id ?? null;
        }

        if (action.meta.arg.agaveEmCostType !== undefined) {
          changes['agaveEmCostType'] = action.meta.arg.agaveEmCostType ?? null;
          changes['agaveEmCostTypeId'] = action.meta.arg.agaveEmCostType?.id ?? null;
        }

        // SEE-974: re-affirm the explicit codingType too, mirroring every other
        // coding field, so the selected type survives a refetch (the override map
        // drives display, but the entity should carry the persisted value).
        if (action.meta.arg.codingType !== undefined) {
          changes['codingType'] = action.meta.arg.codingType;
        }

        if (action.meta.arg.agaveGlCode !== undefined) {
          changes['agaveGlCode'] = action.meta.arg.agaveGlCode ?? null;
          changes['agaveGlCodeId'] = action.meta.arg.agaveGlCode?.id ?? null;
        }

        if (action.meta.arg.flexGlCode !== undefined) {
          changes['flexGlCode'] = action.meta.arg.flexGlCode ?? null;
          changes['flexGlCodeId'] = action.meta.arg.flexGlCode?.id ?? null;
        }

        if (action.meta.arg.flexCode !== undefined) {
          changes['flexCode'] = action.meta.arg.flexCode ?? null;
          changes['flexCodeId'] = action.meta.arg.flexCode?.id ?? null;
        }

        if (action.meta.arg.flexCostCode !== undefined) {
          changes['flexCostCode'] = action.meta.arg.flexCostCode ?? null;
          changes['flexCostCodeId'] = action.meta.arg.flexCostCode?.id ?? null;
        }

        if (action.meta.arg.flexCostType !== undefined) {
          changes['flexCostType'] = action.meta.arg.flexCostType ?? null;
          changes['flexCostTypeId'] = action.meta.arg.flexCostType?.id ?? null;
        }

        if (action.meta.arg.agaveVendor !== undefined) {
          changes['agaveVendor'] = action.meta.arg.agaveVendor ?? null;
          changes['agaveVendorId'] = action.meta.arg.agaveVendor?.id ?? null;
        }

        if (action.meta.arg.agaveSubJob !== undefined) {
          changes['agaveSubJob'] = action.meta.arg.agaveSubJob ?? null;
          changes['agaveSubJobId'] = action.meta.arg.agaveSubJob?.id ?? null;
        }

        if (action.meta.arg.agaveExpense !== undefined) {
          changes['agaveExpense'] = action.meta.arg.agaveExpense ?? null;
          changes['agaveExpenseId'] = action.meta.arg.agaveExpense?.id ?? null;
        }

        if (action.meta.arg.agaveLocation !== undefined) {
          changes['agaveLocation'] = action.meta.arg.agaveLocation ?? null;
          changes['agaveLocationId'] = action.meta.arg.agaveLocation?.id ?? null;
        }

        if (action.meta.arg.agaveDepartment !== undefined) {
          changes['agaveDepartment'] = action.meta.arg.agaveDepartment ?? null;
          changes['agaveDepartmentId'] = action.meta.arg.agaveDepartment?.id ?? null;
        }

        if (action.meta.arg.agaveEmployee !== undefined) {
          changes['agaveEmployee'] = action.meta.arg.agaveEmployee ?? null;
          changes['agaveEmployeeId'] = action.meta.arg.agaveEmployee?.id ?? null;
        }

        if (action.meta.arg.agaveSubaccount !== undefined) {
          changes['agaveSubaccount'] = action.meta.arg.agaveSubaccount ?? null;
          changes['agaveSubaccountId'] = action.meta.arg.agaveSubaccount?.id ?? null;
        }

        if (action.meta.arg.receiptPath !== undefined) {
          changes['receiptPath'] = action.payload.receiptPath;
        }

        if (action.meta.arg.erpApprovalStatus !== undefined) {
          changes['erpApprovalStatus'] = action.payload.erpApprovalStatus;
        }

        if (action.meta.arg.erpBypassStatus !== undefined) {
          changes['erpBypassStatus'] = action.payload.erpBypassStatus;
        }

        // Mirror the backend: a forward action clears the returned state. The mutation
        // doesn't return returnedStatus, so clear it locally instead of via refresh.
        if (action.meta.arg.erpApprovalStatus === true || action.meta.arg.erpBypassStatus === true) {
          changes['returnedStatus'] = null;
        }

        // Fuel capture fields
        if (action.meta.arg.fuelVehicleId !== undefined) {
          changes['fuelVehicleId'] = action.payload.fuelVehicleId;
        }

        if (action.meta.arg.fuelOdometerReading !== undefined) {
          changes['fuelOdometerReading'] = action.payload.fuelOdometerReading;
        }

        if (action.meta.arg.fuelGallons !== undefined) {
          changes['fuelGallons'] = action.payload.fuelGallons;
        }

        if (action.meta.arg.fuelCostPerGallon !== undefined) {
          changes['fuelCostPerGallon'] = action.payload.fuelCostPerGallon;
        }

        if (action.meta.arg.accountingDate !== undefined) {
          changes['accountingDate'] = action.payload.accountingDate;
        }

        // Keyed off the response, not the caller's input: recoding a project moves
        // the row between project groups, and only the server knows which ones own
        // it now. Returned on a project or status write by a paidol using group
        // approvals, so absence means "unchanged", never "none".
        if (action.payload.owningProjectGroupIds !== undefined) {
          changes['owningProjectGroupIds'] = action.payload.owningProjectGroupIds;
          changes['groupApprovalLevels'] = action.payload.groupApprovalLevels;
        }

        if (Object.entries(changes).length > 0) {
          entityAdapter.updateOne(state, {
            id: action.payload.id,
            changes,
          });

          // Also update mobileActions.transactions array if this transaction is in it
          const mobileActionIndex = state.mobileActions.transactions.findIndex(
            (t) => t.id === action.payload.id
          );
          if (mobileActionIndex !== -1) {
            Object.keys(changes).forEach((key) => {
              (state.mobileActions.transactions[mobileActionIndex] as any)[key] = changes[key];
            });
          }
        }
        state.isUpdating = false;
      }
    });

    builder.addCase(refetchTransaction.fulfilled, (state, action) => {
      const { seq, transaction } = action.payload;
      if (!transaction?.id) return;
      if (seq < (state.latestRefetchSeqById[transaction.id] ?? 0)) return;
      entityAdapter.updateOne(state, {
        id: transaction.id,
        changes: transaction,
      });
    });

    builder.addCase(createOrUpdateDirectCost.fulfilled, (state, action) => {
      if (action.payload?.transactionId) {
        const changes: any = {};

        if (action.payload.directCostId !== undefined) {
          changes['procoreDirectCostId'] = action.payload.directCostId;
        }

        if (action.payload.directCostLineItemId !== undefined) {
          changes['procoreDirectCostLineItemId'] = action.payload.directCostLineItemId;
        }

        if (Object.entries(changes).length > 0) {
          entityAdapter.updateOne(state, {
            id: action.payload.transactionId,
            changes,
          });
        }
      }
    });

    builder.addCase(updateReceiptToggle.fulfilled, (state, action) => {
      if (!action.payload?.transaction) return;
      const { seq, transaction: payload } = action.payload;
      if (seq < (state.latestRefetchSeqById[payload.id] ?? 0)) return;
      state.isUpdating = false;
      state.hadErrorUpdating = false;
      const transaction = state.entities[payload.id];
      if (transaction) {
        transaction.approveWithoutReceipts = payload.approveWithoutReceipts;
      }
    });
    builder.addCase(updateNotesToggle.fulfilled, (state, action) => {
      if (!action.payload?.transaction) return;
      const { seq, transaction: payload } = action.payload;
      if (seq < (state.latestRefetchSeqById[payload.id] ?? 0)) return;
      state.isUpdating = false;
      state.hadErrorUpdating = false;
      const transaction = state.entities[payload.id];
      if (transaction) {
        transaction.approveWithoutNotes = payload.approveWithoutNotes;
      }
    });

    // The refetch inside the thunk already merged the row; this only has to end
    // the pending state so the review button re-enables.
    builder.addCase(markErpTransactionReviewed.fulfilled, (state, action) => {
      state.isUpdating = false;
      state.hadErrorUpdating = false;
      const { groupContext } = action.payload;
      if (groupContext) entityAdapter.updateOne(state, { id: action.meta.arg.id, changes: groupContext });
    });

    builder.addCase(updateFlexCodeTransactionStatus.fulfilled, (state, action) => {
      if (!action.payload?.transaction) return;
      const { seq, transaction: payload, groupContext } = action.payload;
      if (seq < (state.latestRefetchSeqById[payload.id] ?? 0)) return;
      state.isUpdating = false;
      state.hadErrorUpdating = false;
      const transaction = state.entities[payload.id];
      if (transaction) {
        if (groupContext) Object.assign(transaction, groupContext);
        transaction.flexCodeTransactionStatus = payload.flexCodeTransactionStatus;
        // A forward review/approve clears the returned state.
        if (
          payload.flexCodeTransactionStatus === FlexCodeTransactionStatus.REVIEWED ||
          payload.flexCodeTransactionStatus === FlexCodeTransactionStatus.APPROVED
        ) {
          transaction.returnedStatus = null;
        }
      }
    });

    builder.addCase(bulkUpdateFlexCodeStatus.fulfilled, (state, action) => {
      const { originalStatuses } = action.meta.arg;

      if (action.payload) {
        const updates = action.payload
          .filter((item) => item.transaction !== undefined)
          .map(({ id, transaction }) => ({
            id: id,
            changes: {
              flexCodeTransactionStatus: transaction!.flexCodeTransactionStatus,
              // A forward review/approve clears the returned state.
              ...((transaction!.flexCodeTransactionStatus === FlexCodeTransactionStatus.REVIEWED ||
                transaction!.flexCodeTransactionStatus === FlexCodeTransactionStatus.APPROVED) && {
                returnedStatus: null,
              }),
            },
          }));

        // Rollback failed updates
        const failedIds = action.payload.filter((item) => !item.transaction).map((item) => item.id);

        const rollbackUpdates = originalStatuses
          .filter((orig) => failedIds.includes(orig.id))
          .map((orig) => ({
            id: orig.id,
            changes: { flexCodeTransactionStatus: orig.status },
          }));

        entityAdapter.updateMany(state, [...updates, ...rollbackUpdates]);
        if (rollbackUpdates.length) state.hadErrorUpdating = true;
        state.isUpdating = false;
      }
    });

    builder.addCase(bulkUpdateFlexCodeStatus.pending, (state, action) => {
      state.isUpdating = true;
      state.hadErrorUpdating = false;
      state.updateError = null;
      // Store original statuses in meta for potential rollback - todo
      // action.meta.arg.originalStatuses = action.payload?.originalStatuses;

      // Apply optimistic update
      const updates = action.meta.arg.ids.map((id) => ({
        id: id,
        changes: { flexCodeTransactionStatus: action.meta.arg.status },
      }));

      entityAdapter.updateMany(state, updates);
    });

    builder.addCase(bulkUpdateFlexCodeStatus.rejected, (state, action) => {
      const rollbackUpdates = action.meta.arg.originalStatuses.map((orig) => ({
        id: orig.id,
        changes: { flexCodeTransactionStatus: orig.status },
      }));

      entityAdapter.updateMany(state, rollbackUpdates);
      state.isUpdating = false;
      state.hadErrorUpdating = true;
      state.updateError = action.error?.message ?? null;
    });

    builder.addMatcher(
      (action) =>
        isAnyOf(
          updateTransaction.pending,
          markErpTransactionReviewed.pending,
          updateFlexCodeTransactionStatus.pending,
          updateTransactionFlexCode.pending,
          updateTransactionFlexCostCode.pending,
          updateTransactionFlexCostType.pending,
          updateTransactionFlexVendor.pending,
          updateTransactionCodingOption.pending,
          updateTransactionFlexGlCode.pending
        )(action),
      (state) => {
        state.isUpdating = true;
        state.hadErrorUpdating = false;
        state.updateError = null;
      }
    );

    builder.addMatcher(
      isAnyOf(
        updateTransaction.rejected,
        markErpTransactionReviewed.rejected,
        updateFlexCodeTransactionStatus.rejected,
        updateTransactionFlexCode.rejected,
        updateTransactionFlexCostCode.rejected,
        updateTransactionFlexCostType.rejected,
        updateTransactionFlexVendor.rejected,
        updateTransactionCodingOption.rejected,
        updateTransactionFlexGlCode.rejected
      ),
      (state, action) => {
        state.isUpdating = false;
        state.hadErrorUpdating = true;
        state.updateError = action.error?.message ?? null;
      }
    );
  },
});

export const {
  resetTransactionsSlice,
  setTransactionPosting,
  applyLocalTransactionUpdates,
  markRefetchSeqDispatched,
  markTransactionResubmitted,
  markSplitFoldedBack,
  clearSplitFoldedBack,
  setCodingTypeOverride,
} = transactionsSlice.actions;

export const selectTransactionsSlice = (state: RootState): TransactionsState =>
  state?.cards?.transactions ?? initialState;

export const { selectAll: selectAllTransactions } = entityAdapter.getSelectors(selectTransactionsSlice);

export const selectMobileActionsTransactions = (state: RootState) =>
  selectTransactionsSlice(state).mobileActions.transactions;

export const selectMobileActionsLoading = (state: RootState) =>
  selectTransactionsSlice(state).mobileActions.loading;

// SEE-1392: branch on `hadErrorLoading`, not on the message — the message is
// best-effort and is null when the rejection carried no Error. (The CDK
// dispatcher raises GENERIC_GRAPHQL_ERROR_MESSAGE, so it is usually present,
// but nothing guarantees it.) Kept as primitives rather than one object-returning
// selector so useAppSelector's reference equality does not re-render on every
// unrelated store change.
//
// `loadError` is diagnostic only — for Redux DevTools and for tests asserting the
// rejection was recorded. Do NOT render it. It is the raw thrown message, so it is
// English-only and names internal schema types and fields ("Not Authorized to
// access paidolId on type V2TransactionFilterOptionsPayload"). Sentry already has
// it from TransactionService.execute; the UI shows translated copy instead.
export const selectHadErrorLoadingTransactions = (state: RootState): boolean =>
  selectTransactionsSlice(state).hadErrorLoading;

export const selectFetchedTransactionCount = (state: RootState): number =>
  selectTransactionsSlice(state).fetchedCount;

export const selectTransactionsLoadError = (state: RootState): string | null =>
  selectTransactionsSlice(state).loadError;

export const selectMobileActionsHadErrorLoading = (state: RootState): boolean =>
  selectTransactionsSlice(state).mobileActions.hadErrorLoading;

export const selectMobileActionsLoadError = (state: RootState): string | null =>
  selectTransactionsSlice(state).mobileActions.loadError;

export const selectTransactionById = (state: RootState, id: string) =>
  selectTransactionsSlice(state).entities[id];

// SEE-974: the "Transaction type" dropdown pick for a transaction (undefined if
// the user hasn't picked one — fall back to deriving from the coded fields).
export const selectCodingTypeOverride = (state: RootState, id: string): string | undefined =>
  selectTransactionsSlice(state).codingTypeOverridesById[id];

export default transactionsSlice.reducer;
